import { Loader2 } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";

import { Eyebrow } from "@/components/eyebrow";
import { PageShell } from "@/components/page-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { trackOnce } from "@/lib/analytics";
import { useAcceptReportInvite, useLogout, useReportInvite } from "@/lib/api";
import { logger } from "@/lib/logger";

/**
 * Redeem a report invite. Public route, not wrapped in `<ProtectedRoute>`:
 * that guard redirects to /login and would strand the token. Instead this page
 * self-gates on the preview's error status, exactly as `/report/:id` does: the
 * 401 branch signs the invitee in and returns them here.
 */
export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const query = useReportInvite(token ?? null);

  const status = (query.error as { status?: number } | null)?.status;
  const returnTo = token ? `/report/invite/${token}` : "/history";

  // Count a successfully loaded invite preview, not a mount, so dead links don't
  // inflate the funnel. Keyed by the token for StrictMode/remount dedupe: the
  // token is the sole credential, so it is only ever the local key, never sent.
  if (query.data && token) {
    trackOnce(token, "shared_link_opened", { method: "invite" });
  }

  return (
    <PageShell>
      {query.isLoading && (
        <div className="flex justify-center py-24">
          <Loader2 className="text-muted h-6 w-6 animate-spin" />
        </div>
      )}
      {query.error != null &&
        (status === 401 ? (
          <SignInCard returnTo={returnTo} />
        ) : status === 403 ? (
          <WrongAccountCard returnTo={returnTo} />
        ) : status === 410 ? (
          <ExpiredCard />
        ) : status === 404 ? (
          <DeadLinkCard />
        ) : (
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-muted-foreground text-sm">
                Couldn’t load this invite. Please try again.
              </p>
              <Button variant="outline" className="mt-4" onClick={() => void query.refetch()}>
                Retry
              </Button>
            </CardContent>
          </Card>
        ))}
      {query.data &&
        (query.data.expired ? (
          <ExpiredCard />
        ) : (
          <InviteBody token={token ?? ""} companyName={query.data.company_name} />
        ))}
    </PageShell>
  );
}

function SignInCard({ returnTo }: { returnTo: string }) {
  return (
    <Card className="mx-auto max-w-md">
      <CardContent className="space-y-5 pt-6">
        <div className="text-center">
          <Eyebrow>Sign in required</Eyebrow>
          <h1 className="text-primary mt-3 text-2xl font-normal tracking-tight">
            Sign in to open this invite.
          </h1>
          <p className="text-muted-foreground mt-3 text-sm">
            An invite is tied to the email address it was sent to. Sign in with that address (or
            create an account for it) and we’ll bring you straight back here.
          </p>
        </div>
        <Button asChild variant="outline" className="w-full">
          <Link to={`/login?returnTo=${encodeURIComponent(returnTo)}`}>Sign in with email</Link>
        </Button>
        <p className="text-muted-foreground text-center text-sm">
          No account yet?{" "}
          <Link
            to={`/signup?returnTo=${encodeURIComponent(returnTo)}`}
            className="text-accent font-medium hover:underline"
          >
            Create one
          </Link>
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * The caller is signed in as somebody else. The API deliberately withholds the
 * invited address in its 403 (echoing it would leak the invitee's email to
 * anyone holding the link) so the copy names no address.
 */
function WrongAccountCard({ returnTo }: { returnTo: string }) {
  const navigate = useNavigate();
  const logoutMutation = useLogout();

  const handleSwitchAccount = async () => {
    await logoutMutation.mutateAsync();
    navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`);
  };

  return (
    <div className="py-12 text-center">
      <Eyebrow>Wrong account</Eyebrow>
      <h1 className="text-primary mt-3 text-4xl font-normal tracking-tight sm:text-5xl">
        This invite was sent to a different email address.
      </h1>
      <p className="text-muted-foreground mt-4">
        You’re signed in with an account the invite wasn’t issued to. Sign in with the address the
        link was sent to, and it will open.
      </p>
      <Button
        className="mt-8"
        disabled={logoutMutation.isPending}
        onClick={() => void handleSwitchAccount()}
      >
        Sign in with a different account
      </Button>
    </div>
  );
}

function ExpiredCard() {
  return (
    <div className="py-12 text-center">
      <Eyebrow>Expired</Eyebrow>
      <h1 className="text-primary mt-3 text-4xl font-normal tracking-tight sm:text-5xl">
        This invite has expired.
      </h1>
      <p className="text-muted-foreground mt-4">
        Ask whoever shared the report to send you a fresh invite.
      </p>
      <Button asChild className="mt-8">
        <Link to="/assess">Start an assessment</Link>
      </Button>
    </div>
  );
}

/**
 * One 404 covers an unknown, a revoked, and an already-redeemed token, so the
 * copy must not claim a cause the API deliberately withholds (same rule as the
 * shared-link 404). An invite you already accepted lands here too, hence the
 * pointer to your history rather than a dead end.
 */
function DeadLinkCard() {
  return (
    <div className="py-12 text-center">
      <Eyebrow>Unavailable</Eyebrow>
      <h1 className="text-primary mt-3 text-4xl font-normal tracking-tight sm:text-5xl">
        This invite isn’t available.
      </h1>
      <p className="text-muted-foreground mt-4">
        It may have been revoked, or the address may be mistyped. If you’ve already accepted it, the
        report is in your history, since an invite link only works once.
      </p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Button asChild>
          <Link to="/history">Go to your history</Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/assess">Start an assessment</Link>
        </Button>
      </div>
    </div>
  );
}

interface InviteBodyProps {
  token: string;
  companyName: string;
}

function InviteBody({ token, companyName }: InviteBodyProps) {
  const navigate = useNavigate();
  const acceptInvite = useAcceptReportInvite(token);

  const handleAccept = () => {
    acceptInvite.mutate(undefined, {
      onSuccess: (data) => {
        if (!data) {
          return;
        }
        logger.debug("[invite] accepted, opening report", data.assessment_id);
        navigate(`/report/${data.assessment_id}`);
      },
    });
  };

  // A live invite can still lose its race: revoked or redeemed between the
  // preview and the click. Name what we know, and no more.
  const acceptStatus = (acceptInvite.error as { status?: number } | null)?.status;

  return (
    <Card className="border-accent mx-auto max-w-md">
      <CardContent className="space-y-5 pt-6 text-center">
        <div>
          <Eyebrow>You’ve been invited</Eyebrow>
          <h1 className="text-primary mt-3 text-3xl font-normal tracking-tight">
            View {companyName}’s AI disruption report.
          </h1>
          <p className="text-muted-foreground mt-3 text-sm">
            Accepting adds this report to your history. The invite works once, and only for your
            account.
          </p>
        </div>

        <Button className="w-full" disabled={acceptInvite.isPending} onClick={handleAccept}>
          {acceptInvite.isPending ? "Accepting…" : "Accept invite"}
        </Button>

        {acceptInvite.isError && (
          <p className="text-destructive text-sm">
            {acceptStatus === 410
              ? "This invite has expired. Ask for a fresh one."
              : acceptStatus === 404
                ? "This invite is no longer available. It may have been revoked or already used."
                : acceptStatus === 403
                  ? "This invite was sent to a different email address."
                  : "Couldn’t accept the invite. Please try again."}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
